import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, EDGE_TYPES, REL_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DEFAULTS = { source_id: "", target_id: "", type: "supports", criticality: "medium", direction: "directed", status: "active", description: "" };

/**
 * RelationDialog — création / édition / consultation d'une RELATIONSHIP persistée.
 * props:
 *  - mode: 'create' | 'edit'
 *  - edgeId: id de l'arête (edit)
 *  - fixedNode: { id, name, role: 'source'|'target' } extrémité verrouillée (create)
 *  - options: [{id,name,type}] pour l'autre extrémité (create)
 *  - onSaved: callback après succès
 */
export default function RelationDialog({ open, onOpenChange, mode, edgeId, fixedNode, options = [], onSaved }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const readOnly = !can("dependency:write");
  const [form, setForm] = useState(DEFAULTS);

  const { data: edge } = useQuery({
    queryKey: ["relation", edgeId],
    queryFn: async () => (await api.get(`/dependencies/${edgeId}`)).data,
    enabled: open && mode === "edit" && !!edgeId,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && edge) {
      setForm({
        source_id: edge.source_id, target_id: edge.target_id, type: edge.type,
        criticality: edge.criticality, direction: edge.direction, status: edge.status,
        description: edge.description || "",
      });
    } else if (mode === "create" && fixedNode) {
      setForm({ ...DEFAULTS, [fixedNode.role === "source" ? "source_id" : "target_id"]: fixedNode.id });
    }
  }, [open, mode, edge, fixedNode]);

  const save = useMutation({
    mutationFn: async () => {
      if (mode === "edit")
        return (await api.put(`/dependencies/${edgeId}`, {
          type: form.type, criticality: form.criticality, direction: form.direction,
          status: form.status, description: form.description,
        })).data;
      return (await api.post("/dependencies", form)).data;
    },
    onSuccess: () => {
      toast.success(mode === "edit" ? "Relation mise à jour" : "Relation créée");
      qc.invalidateQueries();
      onOpenChange(false);
      onSaved?.();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const selectableRole = fixedNode?.role === "source" ? "target_id" : "source_id";
  const fixedName = fixedNode?.name;
  const otherName = (id) => options.find((o) => o.id === id)?.name || edge?.[fixedNode?.role === "source" ? "target_name" : "source_name"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="relation-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">
            {mode === "edit" ? (readOnly ? "Consulter la relation" : "Modifier la relation") : "Nouvelle relation"}
          </DialogTitle>
          <DialogDescription>Relation Actif ↔ Service persistée (RELATIONSHIP).</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Extrémités */}
          {mode === "create" ? (
            <>
              {fixedNode?.role === "target" ? (
                <div>
                  <Label className="text-xs">Source (actif)</Label>
                  <Select value={form.source_id} onValueChange={(v) => setForm({ ...form, source_id: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="relation-source-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent className="max-h-64">{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Destination : <span className="font-mono">{fixedName}</span></p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">Destination</Label>
                  <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="relation-target-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent className="max-h-64">{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Source : <span className="font-mono">{fixedName}</span></p>
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-muted-foreground font-mono">
              {edge?.source_name} → {edge?.target_name}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Type de relation</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })} disabled={readOnly}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="relation-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REL_TYPES.map((t) => <SelectItem key={t} value={t}>{EDGE_TYPES[t]?.label || t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Criticité</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })} disabled={readOnly}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="high">Élevée</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })} disabled={readOnly}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="directed">Dirigée</SelectItem>
                  <SelectItem value="bidirectional">Bidirectionnelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={readOnly}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="planned">Planifiée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} disabled={readOnly} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          {!readOnly && (
            <Button className="rounded-sm" data-testid="save-relation-button"
              onClick={() => save.mutate()}
              disabled={save.isPending || (mode === "create" && (!form.source_id || !form.target_id))}>
              {save.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
